import React, { useEffect, useState, Fragment } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Dot,
  Legend,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { get, post } from "../Libraries/Ajax/HttpService";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useDistrict } from "../utils/DistrictContext";
import { DatePicker, ConfigProvider, theme as antTheme, Spin } from "antd"; // Alias theme to avoid conflict
import { SwapOutlined } from "@ant-design/icons";
import moment from "moment";
export default function AnomalyDetection({
  initialCategory,
  initialStartDate,
  initialEndDate,
  initialTimeRange,
}) {
  const { theme, colors } = useTheme();

  // --- API State Management ---
  const [categories, setCategories] = useState([]);
  const [meterIds, setMeterIds] = useState([]);
  // const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMeters, setSelectedMeters] = useState([]);
  // const [startDate, setStartDate] = useState(
  //   dayjs().subtract(30, "days").format("YYYY-MM-DD"),
  // );
  // const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedCategory, setSelectedCategory] = useState(
    initialCategory || "",
  );
  const [startDate, setStartDate] = useState(
    initialStartDate || dayjs().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    initialEndDate || dayjs().format("YYYY-MM-DD"),
  );
  const [meterSearch, setMeterSearch] = useState("");

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { selectedDistrict } = useDistrict();
  const diffDays = dayjs(endDate).diff(dayjs(startDate), "day");
  const meterText = meterIds.length === 1 ? "anomaly meter" : "anomaly meters";
  // const dayText = diffDays === 1 ? "day" : "days";
  const LINE_COLORS = [colors.primary, "#4a1ee9", "#00C853", "#FF9800"];
  const today = dayjs().format("YYYY-MM-DD");
  const { RangePicker } = DatePicker;
  const [timeRange, setTimeRange] = useState(
    initialTimeRange || "last_30_days",
  );
  const handleRangeClick = (val) => {
    setMeterSearch("");
    setSearchError("");

    if (val === "YTD") {
      const startOfYear = dayjs().startOf("year").format("YYYY-MM-DD");
      const today = dayjs().format("YYYY-MM-DD");

      setStartDate(startOfYear);
      setEndDate(today);
      setTimeRange("YTD");
    } else {
      // Just set the range string.
      // The useEffect above will catch the API response and move the calendar.
      setTimeRange(val);
    }
  };
  const RenderCustomLegend = () => {
    // We use pure white here to ensure it pops on your dark dashboard background
    const whiteColor = "#FFFFFF";

    const uniqueItems = [
      { label: "Flow", dash: "none", type: "flow" },
      { label: "Threshold Value", dash: "5, 5", type: "threshold_val" },
      { label: "Upper Rolling Threshold", dash: "1, 4", type: "roll_thresh" },
      {
        label: "Historical Guardrail",
        dash: "10, 3, 2, 3",
        type: "hist_guard",
      },
    ];

    return (
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {uniqueItems.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            {/* SVG Icon representing the line style */}
            <svg width="32" height="12" className="inline-block">
              <line
                x1="0"
                y1="6"
                x2="32"
                y2="6"
                stroke={whiteColor} // Set line color to white
                strokeWidth="2.5"
                strokeDasharray={item.dash}
                strokeLinecap={item.type === "roll_thresh" ? "round" : "butt"}
              />
            </svg>
            {/* Label Text */}
            <span
              className="text-xs font-semibold"
              style={{ color: whiteColor }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const getDisplayDays = () => {
    if (timeRange === "last_7_days") return 7;
    if (timeRange === "last_week") return 7;
    if (timeRange === "last_30_days") return 30;
    if (timeRange === "last_90_days") return 90;
    if (timeRange === "last_year") return 365;
    return dayjs(endDate).diff(dayjs(startDate), "day") || 0;
  };
  const displayDays = getDisplayDays();
  const dayText = displayDays === 1 ? "day" : "days";

  const timePeriods = [
    { label: "D", value: "past_day" },
    { label: "W", value: "last_7_days" },
    { label: "1M", value: "last_30_days" },
    { label: "3M", value: "last_90_days" },
    // { label: "6M", value: "last_6_months" }, // Or "last_day" depending on your API
    { label: "YTD", value: "YTD" },
    { label: "1Y", value: "last_year" },
  ];
  useEffect(() => {
    if (initialTimeRange) setTimeRange(initialTimeRange);
  }, [initialTimeRange]);
  // 1. Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await get("apis/anoamaly_detection", {
          params: { category: null },
        });
        const cats = res?.data || [];
        setCategories(cats);
        if (cats.length > 0) {
          setSelectedCategory(cats[0].class_display_name || cats[0]);
        }
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCategories();
    // }, [selectedCategory]);
  }, [selectedDistrict]);

  useEffect(() => {
    const fetchMeters = async () => {
      try {
        const isCustom = timeRange === "Custom";

        const res = await get("apis/anoamaly_detection", {
          params: {
            category: selectedCategory,
            start_date: isCustom ? startDate : undefined,
            end_date: isCustom ? endDate : undefined,
            time_range: timeRange, // Will send 'last_90_days' OR 'Custom'
          },
        });

        const meters = res?.data?.data || [];
        if (res?.data?.start_date && res?.data?.end_date && !isCustom) {
          setStartDate(dayjs(res.data.start_date).format("YYYY-MM-DD"));
          setEndDate(dayjs(res.data.end_date).format("YYYY-MM-DD"));
        }
        setMeterIds(meters);

        if (meters.length > 0) {
          setSelectedMeters([String(meters[0].meter_id)]);
        } else {
          setSelectedMeters([]);
        }
      } catch (err) {
        console.error("Error fetching meter IDs", err);
      }
    };

    if (selectedCategory && !meterSearch) fetchMeters();
    // }, [selectedCategory, startDate, endDate, timeRange, selectedDistrict]);
  }, [selectedCategory, timeRange, selectedDistrict]);

  useEffect(() => {
    const searchMeters = async () => {
      //
      try {
        const isCustom = timeRange === "Custom";
        const res = await get("apis/anoamaly_detection", {
          params: {
            meter_id: meterSearch,
            time_range: timeRange,
            // Sending dates but NO category
            start_date: isCustom ? startDate : undefined,
            end_date: isCustom ? endDate : undefined,
          },
        });

        const response = res?.data;

        if (!response || !response.meter_id) {
          setMeterIds([]);
          setSelectedMeters([]);
          setSearchError("Meter ID not available");
          return;
        }

        setSearchError("");

        const meterObj = {
          meter_id: response.meter_id,
          // highest_flow: response?.max?.flow || 0,
          // highest_flow_time: response?.max?.flow_time || null,
          highest_flow: response.anomaly_count > 0 ? response?.max?.flow : null,
          highest_flow_time:
            response.anomaly_count > 0 ? response?.max?.flow_time : null,
        };

        // UPDATE CATEGORY FROM API
        const apiCategory = response?.data?.[0]?.category;
        if (apiCategory) {
          setSelectedCategory(apiCategory);
        }

        setMeterIds([meterObj]);

        // force only searched meter
        setSelectedMeters([]);
        setTimeout(() => {
          setSelectedMeters([String(response.meter_id)]);
        }, 0);
      } catch (err) {
        console.error("Meter search error:", err);

        setMeterIds([]);
        setSelectedMeters([]);
        setSearchError("Meter ID not available");
      }
    };

    const fetchOriginalMeters = async () => {
      try {
        const isCustom = timeRange === "Custom";

        const res = await get("apis/anoamaly_detection", {
          params: {
            category: selectedCategory,
            start_date: isCustom ? startDate : undefined,
            end_date: isCustom ? endDate : undefined,
            time_range: timeRange,
          },
        });

        const meters = res?.data?.data || [];

        setMeterIds(meters);

        if (meters.length > 0) {
          setSelectedMeters([String(meters[0].meter_id)]);
        } else {
          setSelectedMeters([]);
        }
      } catch (err) {
        console.error("Reset meter fetch error:", err);
      }
    };

    if (meterSearch) {
      searchMeters();
    } else {
      setSearchError("");
      fetchOriginalMeters();
    }
  }, [meterSearch, selectedDistrict, timeRange]);

  useEffect(() => {
    // if (selectedMeters.length === 0) {
    //   setChartData([]);
    //   return;
    // }
    if (!selectedCategory || selectedMeters.length === 0) return;
    const fetchChart = async () => {
      setLoading(true);
      try {
        const isCustomMode = timeRange === "Custom" || timeRange === "YTD";
        const apiTimeRange = timeRange === "YTD" ? "Custom" : timeRange;

        const response = await get("apis/anoamaly_detection", {
          params: {
            category: selectedCategory,
            meter_id: selectedMeters.join(","),
            start_date: isCustomMode ? startDate : undefined,
            end_date: isCustomMode ? endDate : undefined,
            time_range: apiTimeRange, // Sends "Custom" to override default 90-day logic
          },
        });

        const datasets = response.data || [];
        const merged = {};

        datasets.forEach((meterObj) => {
          const meterId = String(meterObj.meter_id);
          (meterObj.data || []).forEach((row) => {
            const parsed = dayjs(row.day, "MMM DD, YYYY");
            const dayKey = parsed.format("YYYY-MM-DD");
            if (!merged[dayKey]) {
              merged[dayKey] = {
                date: parsed.format("DD MMM ,YYYY"),
                fullDate: row.day,
              };
            }
            merged[dayKey][meterId] = Number(row.total_daily_flow);
            merged[dayKey][`anomaly_${meterId}`] = row.anomaly;
            merged[dayKey][`reason_${meterId}`] = row.anomaly_reason;
            // ENSURE THESE NAMES MATCH THE JSX BELOW EXACTLY
            merged[dayKey][`threshold_val_${meterId}`] = Number(
              row.threshold_value,
            );
            merged[dayKey][`roll_thresh_${meterId}`] = Number(
              row.upper_rolling_threshold,
            );
            merged[dayKey][`hist_guard_${meterId}`] = Number(
              row.historical_guardrail_upper,
            );
            merged[dayKey][`suppressed_${meterId}`] =
              row.anomaly_suppressed_reason;
          });
        });

        setChartData(
          Object.keys(merged)
            .sort()
            .map((k) => merged[k]),
        );
      } catch (err) {
        console.error("Chart Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChart();
  }, [
    startDate,
    endDate,
    selectedCategory,
    selectedMeters,
    timeRange,
    selectedDistrict,
  ]);
  useEffect(() => {
    if (initialCategory && categories.length > 0) {
      setSelectedCategory(initialCategory);
    }
  }, [categories, initialCategory]);

  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };
  // --- Handler for RangePicker ---
  const handleRangeChange = (dates, dateStrings) => {
    if (dateStrings && dateStrings[0] && dateStrings[1]) {
      setStartDate(dateStrings[0]);
      setEndDate(dateStrings[1]);
    } else {
      // Optional: reset to defaults or null
      setStartDate("");
      setEndDate("");
    }
  };
  // Custom Tooltip Renderer
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="p-3 border rounded-lg shadow-lg min-w-[240px]"
          style={{
            backgroundColor: theme === "white" ? "#ffffff" : colors.background,
            borderColor: `${colors.primary}30`,
            boxShadow: `0 8px 32px ${colors.primary}30`,
            backdropFilter: "blur(6px)",
          }}
        >
          <p
            className="font-bold mb-3 text-sm border-b pb-2"
            style={{ color: colors.text, borderColor: colors.border }}
          >
            {label}
          </p>

          {payload.map((entry, index) => {
            // entry.dataKey is the raw meterId (e.g. "145330")
            const mId = entry.dataKey;
            const meterData = entry.payload;

            // Only process the main "Flow" lines.
            // We ignore entries where the dataKey starts with threshold or roll
            if (typeof mId !== "string" || mId.includes("_")) return null;

            const isAnomaly = meterData[`anomaly_${mId}`] === 1;
            const anomalyReason = meterData[`reason_${mId}`];
            const suppressedReason = meterData[`suppressed_${mId}`];

            return (
              <div key={`tooltip-${mId}`} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span
                    className="text-xs font-bold"
                    style={{ color: colors.text }}
                  >
                    Meter {mId}:
                  </span>
                  <span
                    className="text-xs font-bold"
                    style={{ color: colors.primary }}
                  >
                    {Number(entry.value).toLocaleString("en-US")} Gal
                  </span>
                </div>

                {/* 1. Show the Suppression info if it exists */}
                {suppressedReason && (
                  <div className="ml-4 mt-1 border-l-2 border-gray-500 pl-2">
                    <p
                      className="text-[11px] leading-tight italic"
                      style={{ color: "#94a3b8" }}
                    >
                      <span className="font-bold text-gray-400">
                        Suppressed:{" "}
                      </span>
                      {suppressedReason}
                    </p>

                    {/* Show the flag reason even if suppressed */}
                    {anomalyReason && (
                      <p
                        className="text-[10px] mt-0.5"
                        style={{ color: "#f87171", opacity: 0.8 }}
                      >
                        <span className="font-semibold">Flagged for: </span>
                        {anomalyReason}
                      </p>
                    )}
                  </div>
                )}

                {/* 2. Show the active Anomaly info only if NOT suppressed */}
                {isAnomaly && !suppressedReason && (
                  <div className="ml-4 mt-1 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle size={12} style={{ color: "#ef4444" }} />
                      <span className="text-[10px] font-bold uppercase text-red-500 tracking-tight">
                        Anomaly Detected
                      </span>
                    </div>
                    {anomalyReason && (
                      <p
                        className="text-[11px] leading-tight pl-4"
                        style={{ color: "#f87171" }}
                      >
                        <span className="font-semibold text-red-300">
                          Reason:{" "}
                        </span>
                        {anomalyReason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };
  const toggleMeter = (id) => {
    const meterId = String(id);
    if (selectedMeters.includes(meterId)) {
      setSelectedMeters(selectedMeters.filter((m) => m !== meterId));
    } else {
      if (selectedMeters.length >= 4) {
        toast.error("Maximum 4 meters allowed");
        return;
      }
      setSelectedMeters([...selectedMeters, meterId]);
    }
  };

  return (
    <div className="space-y-4 min-w-0">
      {/* Top Row: Controls */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"> */}
      <div className="flex justify-between gap-4 flex-wrap">
        <div className="shrink-0 w-full lg:w-[320px] space-y-2.5">
          {/* METER SEARCH */}
          <label
            className="block text-xs mb-2 font-semibold"
            style={{ color: colors.primary }}
          >
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search meter..."
              value={meterSearch}
              onChange={(e) => setMeterSearch(e.target.value)}
              className="w-full px-3 py-3 pr-10 border rounded-lg text-sm focus:outline-none"
              style={{
                background: colors.background,
                borderColor: colors.border,
                color: colors.text,
              }}
            />

            {meterSearch && (
              <button
                onClick={() => setMeterSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-lg"
                style={{ color: colors.textSecondary }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="flex items-end gap-4 flex-wrap">
          {/* CATEGORY SELECT */}
          <div>
            <label
              className="block text-xs mb-2 font-semibold"
              style={{ color: colors.primary }}
            >
              Category
            </label>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedMeters([]);
                  setMeterSearch("");
                }}
                className="w-full px-3 py-2.5 pr-8 border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none"
                style={{
                  background: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                {categories.map((cat) => (
                  <option
                    key={cat.mizuwatch_code || cat}
                    value={cat.class_display_name || cat}
                  >
                    {cat.class_display_name || cat}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none"
                style={{ color: colors.textSecondary }}
              />
            </div>
          </div>

          {/* NEW SEGMENTED DATE RANGE & CALENDAR */}
          <div>
            {/* <label
              className="block text-xs mb-2 font-semibold"
              style={{ color: colors.primary }}
            >
              Range | Interval | Custom
            </label> */}
            <div
              className="flex items-center gap-1 p-1 rounded-xl border flex-wrap cursor-pointer"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {timePeriods.map((tp) => (
                <button
                  key={tp.value}
                  // onClick={() => setTimeRange(tp.value)}
                  onClick={() => handleRangeClick(tp.value)}
                  className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                  style={{
                    backgroundColor:
                      timeRange === tp.value
                        ? `${colors.primary}20`
                        : "transparent",
                    color:
                      timeRange === tp.value
                        ? colors.primary
                        : colors.textSecondary,
                    border:
                      timeRange === tp.value
                        ? `1px solid ${colors.primary}40`
                        : "1px solid transparent",
                  }}
                >
                  {tp.label}
                </button>
              ))}

              {/* Vertical Separator */}
              <div
                className="h-6 w-[1px] mx-1"
                style={{ backgroundColor: colors.border }}
              />

              {/* Calendar Integrated */}
              <ConfigProvider
                theme={{
                  algorithm:
                    theme === "white"
                      ? antTheme.defaultAlgorithm
                      : antTheme.darkAlgorithm,
                  token: { colorPrimary: colors.primary },
                }}
              >
                <RangePicker
                  value={[
                    startDate ? dayjs(startDate) : null,
                    endDate ? dayjs(endDate) : null,
                  ]}
                  onChange={(dates, strings) => {
                    setMeterSearch("");
                    setSearchError("");
                    handleRangeChange(dates, strings);
                    setTimeRange("Custom");
                  }}
                  disabledDate={disabledDate}
                  placeholder={["Start", "End"]}
                  variant="borderless"
                  separator={
                    <SwapOutlined style={{ color: colors.textSecondary }} />
                  }
                  className="custom-range-picker cursor-pointer"
                  style={{ width: "220px", height: "32px" }}
                  classNames={{
                    popup: {
                      root: `mizu-calendar-popup calendar-${theme}`,
                    },
                  }}
                />
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Row */}
      <div className="flex flex-col lg:flex-row gap-4 min-w-0 items-stretch h-[550px] min-[1921px]:h-[900px] mb-[25px]">
        {/* Left Column - List */}
        <div className="shrink-0 w-full lg:w-[320px] space-y-2.5 h-full flex flex-col">
          {!meterSearch && (
            <div
              className="backdrop-blur-sm border rounded-lg p-4"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: colors.border,
              }}
            >
              {meterIds.length === 0 ? (
                <p
                  className="text-sm font-semibold text-left"
                  style={{ color: colors.textSecondary }}
                >
                 No anomalies detected during the selected period.
                </p>
              ) : (
                <>
                  <p className="text-sm mb-3" style={{ color: colors.text }}>
                    <span className="font-bold">
                      {meterIds.length}{" "}
                      {meterIds.length === 1
                        ? "anomaly meter"
                        : "anomaly meters"}
                    </span>{" "}
                    detected during the selected{" "}
                    <span className="font-bold">
                      {displayDays} {dayText}
                    </span>
                  </p>
                  {selectedMeters.length > 0 && (
                    <button
                      onClick={() => setSelectedMeters([])}
                      className="text-sm font-semibold transition-colors flex items-center gap-1 hover:opacity-80"
                      style={{ color: "#ef4444" }}
                    >
                      <span className="text-base">×</span> Clear
                    </button>
                  )}
                </>
              )}
            </div>
          )}
          {searchError && (
            <div
              className="border rounded-lg p-4 text-sm font-semibold"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: "#ef4444",
                color: "#ef4444",
              }}
            >
              {searchError}
            </div>
          )}

          <div className="space-y-2.5 flex-1 overflow-y-auto pr-1 custom-scrollbar">
            {meterIds.map((meter) => {
              const id = String(meter.meter_id || meter);
              const isSelected = selectedMeters.includes(id);
              return (
                <div
                  key={id}
                  onClick={() => toggleMeter(id)}
                  className="backdrop-blur-sm border rounded-lg p-3.5 cursor-pointer transition-all hover:shadow-md"
                  style={{
                    backgroundColor: isSelected
                      ? `${colors.primary}15`
                      : colors.cardBg,
                    borderColor: isSelected ? colors.primary : colors.border,
                  }}
                >
                  <div className="flex items-start justify-between mb-1.5">
                    <span
                      className="text-base font-bold"
                      style={{ color: colors.text }}
                    >
                      {id}
                    </span>

                    <div className="text-right">
                      <span
                        className="text-lg font-bold block"
                        style={{ color: colors.primary }}
                      >
                        {Number(meter.highest_flow || 0).toLocaleString()}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: colors.textSecondary }}
                      >
                        gallons
                      </span>
                    </div>
                  </div>
                  <p
                    className="text-xs"
                    style={{ color: colors.textSecondary }}
                  >
                    Peak:{" "}
                    {meter.highest_flow_time
                      ? dayjs(meter.highest_flow_time).format("MMM DD, YYYY")
                      : "N/A"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column - Chart */}
        <div
          className="flex-1 mb-4 backdrop-blur-sm border rounded-lg p-6 relative h-[550px] min-[1921px]:h-[900px] max-[767px]:overflow-x-scroll"
          style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
        >
          {loading && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg backdrop-blur-[2px]"
              style={{ background: `${colors.cardBg}B3` }} // 70% opacity version of card background
            >
              <Spin size="large" />
              <span
                className="text-sm font-medium animate-pulse"
                style={{ color: colors.textSecondary }}
              >
                Syncing system data...
              </span>
            </div>
          )}
          <div className="max-[767px]:w-[600px] w-full h-full max-[767px]:h-[550px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis
                  dataKey="date"
                  // tick={{ fill: colors.textSecondary, fontSize: 11 }}
                  stroke={colors.textSecondary}
                  interval="preserveStartEnd"
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  label={{
                    value: "Gallons",
                    angle: -90,
                    position: "insideLeft",
                    offset: 1,
                    fill: colors.textSecondary,
                    fontSize: 12,
                  }}
                  width={80}
                  tick={{ fill: colors.textSecondary, fontSize: 11 }}
                  stroke={colors.textSecondary}
                  tickFormatter={(value) => value.toLocaleString("en-US")}
                />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Legend
                  content={<RenderCustomLegend />}
                  verticalAlign="top"
                  height={45}
                />

                {/* MAP EACH LINE CATEGORY SEPARATELY TO KEEP THEM AS DIRECT CHILDREN */}
                {selectedMeters.map((mId, idx) => (
                  <Line
                    key={`flow-${mId}`}
                    name={`Flow ${mId}`}
                    type="monotone"
                    dataKey={mId}
                    stroke={LINE_COLORS[idx] || colors.primary}
                    strokeWidth={2}
                    dot={(props) => {
                      const { key, ...rest } = props; 
                      const isAnomaly = props.payload[`anomaly_${mId}`] === 1;
                      return isAnomaly ? (
                        <Dot key={key} {...rest} r={5} fill="#ef4444" stroke="#fff" />
                      ) : (
                        <Dot key={key} {...rest} r={2} />
                      );
                    }}
                  />
                ))}

                {selectedMeters.map((mId, idx) => (
                  <Line
                    key={`thresh-${mId}`}
                    name={`Threshold Value ${mId}`}
                    type="monotone"
                    dataKey={`threshold_val_${mId}`}
                    stroke={LINE_COLORS[idx] || colors.primary}
                    strokeDasharray="5 5"
                    dot={false}
                    opacity={0.6}
                    connectNulls
                  />
                ))}

                {selectedMeters.map((mId, idx) => (
                  <Line
                    key={`roll-${mId}`}
                    name={`Upper Rolling Threshold ${mId}`}
                    type="monotone"
                    dataKey={`roll_thresh_${mId}`}
                    stroke={LINE_COLORS[idx] || colors.primary}
                    strokeDasharray="1 4"
                    strokeLinecap="round"
                    strokeWidth={2}
                    dot={false}
                    opacity={0.6}
                    connectNulls
                  />
                ))}

                {selectedMeters.map((mId, idx) => (
                  <Line
                    key={`hist-${mId}`}
                    name={`Historical Guardrail ${mId}`}
                    type="monotone"
                    dataKey={`hist_guard_${mId}`}
                    stroke={LINE_COLORS[idx] || colors.primary}
                    strokeDasharray="10 3 2 3"
                    dot={false}
                    opacity={0.6}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mt-0 mb-4">
            {selectedMeters.map((mId, idx) => (
              <div key={mId} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: LINE_COLORS[idx] }}
                />
                <span
                  className="text-xs font-semibold"
                  style={{ color: colors.text }}
                >
                  {mId}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
